import type { ReactNode } from 'react';
import { Component as ReactComponent } from 'react';

type ErrorBoundaryProps = {
  children: ReactNode;
  fallback?: (error: Error, reset: () => void) => ReactNode;
  onError?: (error: Error, errorInfo: { componentStack: string }) => void;
};

type ErrorBoundaryState = {
  hasError: boolean;
  error: Error | null;
};

/**
 * ErrorBoundary component that catches React errors in its children tree
 * and displays a fallback UI instead of crashing the entire application.
 *
 * @example
 * ```tsx
 * <ErrorBoundary fallback={(error, reset) => <div>Error: {error.message}</div>}>
 *   <MyComponent />
 * </ErrorBoundary>
 * ```
 */
export class ErrorBoundary extends ReactComponent<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: { componentStack: string }) {
    // Call optional onError callback
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Log error for debugging
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  reset = () => {
    this.setState({
      hasError: false,
      error: null,
    });
  };

  render() {
    if (this.state.hasError && this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.reset);
      }
      // Default fallback if none provided
      return (
        <div className="flex flex-col items-center justify-center p-8 rounded border border-red-50 bg-red-10">
          <h3 className="text-sm font-semibold text-red-50 mb-2">Something went wrong</h3>
          <p className="text-xs text-grey-80 mb-4">{this.state.error.message}</p>
          <button
            type="button"
            onClick={this.reset}
            className="px-3 py-1.5 text-xs font-medium rounded border border-red-50 bg-white text-red-50 hover:bg-red-10 focus-ring"
          >
            Try again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
